import { DEFAULT_ROUTINE_ICON, useStores, type Routine } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { EntityIcon, iconTintStyle } from '@/components/ui/icon-picker';
import { Markdown } from '@/components/ui/smoothdown/Markdown';
import {
  FeedRowAction,
  FeedRowCard,
  FeedTrayCta,
} from '@/features/action-cards-view/feed';
import { MARKDOWN_CLASS } from '@/features/action-cards-view/registry/tokens';
import {
  routineReadState,
  useRoutineReportDetail,
} from '@/features/routines/use-routine-refresh';
import { useTabs } from '@/features/tabs';
import {
  latestRoutineReport,
  routineReportLabel,
  routineReportOf,
  type ReadyReport,
} from '@/features/widgets/routine-report';
import { useScrollFade } from '@/hooks/useScrollFade';

import { HomeRoutineArtifacts } from './HomeRoutineArtifacts';
import { HomeRoutineArtifactThumb } from './HomeRoutineArtifactThumb';

type HomeRoutineRowProps = {
  readonly routineId: number;
  readonly title: string;
  readonly routine: Routine | undefined;
  readonly open: boolean;
  readonly onToggle: () => void;
};

const RoutineReportText = ({ report }: { report: ReadyReport }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { maskStyle } = useScrollFade(scrollRef, { fadeSize: 24 });
  return (
    <div
      ref={scrollRef}
      style={maskStyle}
      className="max-h-[60vh] overflow-y-auto"
    >
      <Markdown
        id={report.markdownId}
        content={report.body}
        className={MARKDOWN_CLASS}
      />
    </div>
  );
};

export const HomeRoutineRow = observer(
  ({ routineId, title, routine, open, onToggle }: HomeRoutineRowProps) => {
    const { reports } = useStores();
    const { openTab } = useTabs();
    const latest = latestRoutineReport(reports.orderedReports, routineId);
    useRoutineReportDetail(latest);
    const report = routineReportOf(!routineReadState.hasLoadedReports, latest);
    const icon = routine?.displayIcon ?? DEFAULT_ROUTINE_ICON;
    const openRoutine = () =>
      openTab({ path: `/routines/${routineId}` }, { reuseExisting: true });
    const openAction = (
      <FeedRowAction
        label="Open routine"
        accessibleLabel={`Open ${title}`}
        labelled
        onClick={openRoutine}
      />
    );
    const openPill = (
      <FeedTrayCta
        icon={Icons.routines}
        label="Open"
        accessibleLabel={`Open routine ${title}`}
        onClick={openRoutine}
      />
    );

    return (
      <FeedRowCard
        title={title}
        age={report.kind === 'ready' ? report.scheduledAt.toISOString() : null}
        icon={
          <span
            className="flex size-[var(--ac-row-tile)] items-center justify-center rounded-md text-foreground-muted"
            style={iconTintStyle(icon.color)}
          >
            <EntityIcon
              icon={icon}
              size="var(--ac-row-glyph)"
              className="size-[var(--ac-row-glyph)]"
            />
          </span>
        }
        appName={title}
        label={routineReportLabel(report)}
        labelAccessory={
          report.kind === 'ready' ? (
            <HomeRoutineArtifactThumb report={report.source} />
          ) : undefined
        }
        hoverActions={openPill}
        frame={
          report.kind === 'ready'
            ? {
                open,
                onToggle,
                title: report.heading ?? title,
                label: 'Latest report',
                headerActions: openAction,
                body: (
                  <>
                    <RoutineReportText report={report} />
                    <HomeRoutineArtifacts report={report.source} />
                  </>
                ),
              }
            : undefined
        }
      />
    );
  }
);
